import React, { useEffect, useState } from 'react';
import DashboardHeader from './dashboard/DashboardHeader';
import DashboardMetrics from './dashboard/DashboardMetrics';
import DashboardRevenueChart from './dashboard/DashboardRevenueChart';
import DashboardActivityFeed from './dashboard/DashboardActivityFeed';
import DashboardAlerts from './dashboard/DashboardAlerts';
import DashboardQuickActions from './dashboard/DashboardQuickActions';
import { api } from '../services/api';
import { MetricData, ChartData, ActivityEvent, RevenueAnomaly } from '../types';
import { useCurrency } from './CurrencyContext';

const initialMetrics: MetricData = {
  mrr: 0,
  arr: 0,
  churnRate: 0,
  activeUsers: 0,
  revenueGrowth: 0,
  recoveredRevenue: 0,
  totalCustomers: 0,
  activeSubscriptions: 0,
  revenue: 0,
  arpu: 0,
  nrr: 100,
  momGrowth: 0,
  grossMargin: 0,
  targetArr: 0,
};

const Dashboard: React.FC = () => {
  const { currencySymbol } = useCurrency();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [metrics, setMetrics] = useState<MetricData>(initialMetrics);
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [anomalies, setAnomalies] = useState<RevenueAnomaly[]>([]);
  const [showForecast, setShowForecast] = useState(true);
  const [loading, setLoading] = useState(false);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [metricRes, chartRes, anomRes, eventRes] = await Promise.all([
        api.analytics.getDashboard(selectedDate).catch(() => null),
        api.analytics.getRevenueChart(selectedDate).catch(() => []),
        api.analytics.getAnomalies().catch(() => []),
        api.analytics.getActivityPulse().catch(() => []),
      ]);
      if (metricRes) setMetrics(metricRes);
      if (chartRes && Array.isArray(chartRes)) setChartData(chartRes);
      if (anomRes && Array.isArray(anomRes)) setAnomalies(anomRes);
      if (eventRes && Array.isArray(eventRes)) setEvents(eventRes);
    } catch (error) {
      console.warn('Dashboard data fetch partial', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();

    const handleReset = () => {
      loadDashboardData();
    };

    window.addEventListener('subscale_data_reset', handleReset);
    return () => window.removeEventListener('subscale_data_reset', handleReset);
  }, [selectedDate]);

  return (
    <div className="space-y-6 animate-in fade-in duration-300 pb-16">
      {/* Header with Title, Date Range & Forecast Toggle */}
      <DashboardHeader
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        recoveredRevenue={metrics.recoveredRevenue || 0}
        currencySymbol={currencySymbol}
        showForecast={showForecast}
        onToggleForecast={() => setShowForecast(!showForecast)}
        onRefresh={loadDashboardData}
        loading={loading}
      />

      {/* Top 5 Core Revenue & Retention Metrics */}
      <DashboardMetrics metrics={metrics} />

      {/* Quick Navigation Shortcuts */}
      <DashboardQuickActions />

      {/* Main Analytics: Growth Trajectory Chart & Activity Stream */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 min-w-0">
          <DashboardRevenueChart
            chartData={chartData}
            showForecast={showForecast}
            currencySymbol={currencySymbol}
            targetArr={metrics.targetArr || (metrics.arr ? Math.round(metrics.arr * 1.15) : 0)}
            momGrowth={metrics.momGrowth ?? (metrics.revenueGrowth || 0)}
            grossMargin={metrics.grossMargin || 0}
          />
        </div>
        <div className="min-w-0">
          <DashboardActivityFeed
            events={events}
            currencySymbol={currencySymbol}
          />
        </div>
      </div>

      {/* Automated Revenue Alerts & Signals */}
      <DashboardAlerts
        anomalies={anomalies}
        currencySymbol={currencySymbol}
      />
    </div>
  );
};

export default Dashboard;
